import { colors } from "../../../theme/colors";
// Option background precedence, shared by every style object below:
// isSelected is tested BEFORE isFocused on purpose. A selected row keeps its own
// tint while keyboard focus sits on it, so focus is never on an untinted row —
// verified in-app. Reversing this to "focus always wins" is a design decision,
// not a bug fix.
//
// Default style
//
// A selected filter's chip, per design: Teal T20 fill, Brand Text Primary text,
// Strata/Body/SM type, 3px radius. Defined once and spread into every part of
// the chip — the value container and the clear "x" render as two halves of one
// pill, so they have to agree. Every filter dropdown in the app uses the
// routeFilter variant, which makes this the single place that styles them, and
// the nav's timezone picker wears the same chip.
// A filter dropdown says whether it is in use through its border: Teal T30 while
// it holds no value, UI/Status/Success once it does — the same green as the
// selected chip's own state, so an active filter reads as active from across the
// row without opening it.
export const FILTER_BORDER_IDLE = colors["teal-t30"];
export const FILTER_BORDER_ACTIVE = colors["ui-status-success"];
const filterBorder = (hasValue: boolean) => hasValue ? FILTER_BORDER_ACTIVE : FILTER_BORDER_IDLE;

// The teal tints, under the names the palette gives them (tailwind.config.js).
// react-select styles are emotion objects, so they need values rather than class
// names; naming them here keeps the values in one place instead of repeated as
// literals down the file.
export const TEAL_T10 = colors["ui-background-tertiary-background"];
export const TEAL_T20 = colors["ui-cta-secondary-blue"];

export const SELECTED_FILTER_CHIP = {
    backgroundColor: TEAL_T20,
    color: colors["ui-text-primary-text"],
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: "21px",
};

// Every dropdown's option list uses two colours and white, not three: Teal T20
// marks what is SELECTED (the same fill the chip above carries) and Teal T10
// the option under the cursor. Previously a blue focus tint sat alongside a
// second, different green for the selection.
const OPTION_SELECTED = TEAL_T20;
const OPTION_HOVER = TEAL_T10;

export const defaultStyle = {
    option: (provided: any, state: any) => ({
        ...provided,
        color: colors["unlisted-dropdown-text"],
        backgroundColor: state.isSelected ? OPTION_SELECTED : state.isFocused ? OPTION_HOVER : colors["ui-background-elevated-component"],
        ":hover": {
            backgroundColor: state.isSelected ? OPTION_SELECTED : OPTION_HOVER,
            color: colors["unlisted-dropdown-text"],
        },
        cursor: "pointer",
    }),
    menu: (provided: any) => ({
        ...provided,
        backgroundColor: colors["ui-background-elevated-component"],
        boxShadow: `0px 4px 12px ${colors["unlisted-scrim-15"]}`,
        zIndex: 10,
    }),
    control: (provided: any, state: any) => ({
        ...provided,
        backgroundColor: colors["ui-background-elevated-component"],
        borderColor: colors["unlisted-gray-lines"],
        boxShadow: state.isFocused ? `0 0 0 1px ${colors["ui-cta-primary"]}` : "none",
        "&:hover": {
            borderColor: colors["ui-cta-primary"],
        },
        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    }),
    singleValue: (provided: any) => ({
        ...provided,
        color: colors["unlisted-dropdown-text"],
    }),
    input: (provided: any) => ({
        ...provided,
        color: colors["unlisted-dropdown-text"],
    }),
    valueContainer: (provided: any) => ({
        ...provided,
        padding: "2px 8px",
    }),
    indicatorSeparator: (provided: any) => ({
        ...provided,
        backgroundColor: colors["unlisted-gray-lines"],
    }),
    dropdownIndicator: (provided: any) => ({
        ...provided,
        color: colors["unlisted-secondary-gray"],
        "&:hover": {
            color: colors["ui-cta-primary"],
        },
    }),
};


export const languageSelectStyle = {
    option: (provided: any, state: any) => ({
        ...provided,
        color: colors["unlisted-dropdown-text"],
        backgroundColor: state.isSelected ? OPTION_SELECTED : state.isFocused ? OPTION_HOVER : colors["ui-background-elevated-component"],
        ":hover": {
            backgroundColor: state.isSelected ? OPTION_SELECTED : OPTION_HOVER,
            color: colors["unlisted-dropdown-text"],
        },
        cursor: "pointer"
    }),
    input: (provided: any) => ({
        ...provided,
        color: colors["unlisted-dropdown-text"],
    }),
    menu: (provided: any) => ({
        ...provided,
        backgroundColor: colors["ui-background-elevated-component"], // white for light mode
        width: "fit-content",
        minWidth: "auto"
    }),
    control: (provided: any, state: any) => ({
        ...provided,
        padding: "0px 0px",
        color: colors["unlisted-dropdown-text"],
        backgroundColor: colors["ui-background-elevated-component"], // white for light mode
        border: "none", // No border in light mode
        boxShadow: state.menuIsOpen ? `0 0 0 1px ${colors["ui-cta-primary"]}` : "none",
        ":hover": {
            borderColor: state.menuIsOpen ? colors["ui-cta-primary"] : provided.borderColor,
        },
        width: "fit-content",
        height: "fit-content",
        cursor: "pointer"
    }),
    valueContainer: (provided: any) => ({
        ...provided,
        padding: "0px 0px 0px 8px ", // Adjust the padding to decrease space
    }),
    dropdownIndicator: (provided: any, state: any) => ({
        ...provided,
        color: colors["unlisted-secondary-gray"],
        padding: "0px 8px 0px 1px",
        visibility: state.isDisabled ? "hidden" : "visible",
        ":hover": {
            color: colors["ui-cta-primary"],
        }
    }),
    indicatorsContainer: (provided: any) => ({
        ...provided,
        padding: "0px",
        margin: "0px",
    }),
    indicatorSeparator: (provided: any, state: any) => ({
        ...provided,
        visibility: "hidden",
    }),
    singleValue: (provided: any) => ({
        ...provided,
        color: colors["unlisted-slate-grey"]
    }),
};

export const timezoneStyle = {
    option: (provided: any, state: any) => ({
        ...provided,
        color: colors["unlisted-dropdown-text"],
        backgroundColor: state.isSelected ? OPTION_SELECTED : state.isFocused ? OPTION_HOVER : colors["ui-background-elevated-component"], // white for light mode
        ":hover": {
            backgroundColor: state.isSelected ? OPTION_SELECTED : OPTION_HOVER,
            color: colors["unlisted-dropdown-text"],
        },
        cursor: "pointer"
    }),
    menu: (provided: any) => ({
        ...provided,
        backgroundColor: colors["ui-background-elevated-component"], // white for light mode
        width: "auto",
        right: 0
    }),
    control: (provided: any, state: any) => ({
        ...provided,
        padding: "0px 0px",
        color: colors["unlisted-dropdown-text"],
        backgroundColor: colors["ui-background-elevated-component"], // white for light mode
        border: "none", // No border in light mode
        boxShadow: state.menuIsOpen ? `0 0 0 1px ${colors["ui-cta-primary"]}` : "none",
        ":hover": {
            borderColor: state.menuIsOpen ? colors["ui-cta-primary"] : provided.borderColor,
        },
        width: "fit-content",
        height: "fit-content",
        cursor: "pointer"
    }),
    input: (provided: any) => ({
        ...provided,
        color: colors["greyscale-5"], // Match surrounding text color
    }),
    valueContainer: (provided: any) => ({
        ...provided,
        padding: "0px 0px 0px 8px ", // Adjust the padding to decrease space
    }),
    dropdownIndicator: (provided: any, state: any) => ({
        ...provided,
        color: colors["greyscale-0"], // Black for light mode
        padding: "0px 8px 0px 1px",
        visibility: state.isDisabled ? "hidden" : "visible",
        ":hover": {
            color: colors["greyscale-0"], // Black for light mode
        }
    }),
    indicatorsContainer: (provided: any) => ({
        ...provided,
        padding: "0px",
        margin: "0px",
    }),
    indicatorSeparator: (provided: any, state: any) => ({
        ...provided,
        visibility: "hidden",
    }),
    singleValue: (provided: any) => ({
        ...provided,
        color: colors["greyscale-5"]
    }),
}

export const activityLogStyle = {
    option: (provided: any, state: any) => ({
        ...provided,
        backgroundColor: state.isSelected ? OPTION_SELECTED : state.isFocused ? OPTION_HOVER : colors["ui-background-elevated-component"],
        color: state.isSelected ? colors["unlisted-dropdown-selected-text"] : colors["unlisted-dropdown-text"],
        cursor: "pointer",
        ":hover": {
            backgroundColor: state.isSelected ? OPTION_SELECTED : OPTION_HOVER,
        }
    }),
    control: (provided: any, state: any) => ({
        ...provided,
        backgroundColor: colors["ui-background-elevated-component"], // Explicitly set to white in light mode
        // Add green border when there's a selected value
        borderColor: state.hasValue ? colors["ui-cta-primary"] : colors["unlisted-gray-lines"],
        boxShadow: state.isFocused
            ? (state.hasValue ? `0 0 0 0.25px ${colors["ui-cta-primary"]}` : `0 0 0 0.25px ${colors["unlisted-gray-lines"]}`)
            : "none",
        "&:hover": {
            borderColor: state.hasValue ? colors["ui-cta-primary"] : colors["unlisted-pale-border-grey"], // Darker on hover
        },
        transition: "border-color 0.2s ease, box-shadow 0.2s ease"
    }),
    valueContainer: (provided: any) => ({
        ...provided,
        padding: "4px 8px",
    }),
    singleValue: (provided: any) => ({
        ...provided,
        backgroundColor: "transparent", // No background on the container
        padding: 0,
        "& span": { // Style the inner text span
            padding: "2px 6px",
            borderRadius: "3px",
        }
    }),
    dropdownIndicator: (provided: any) => ({
        ...provided,
        color: colors["unlisted-secondary-gray"],
        '&:hover': {
            color: colors["ui-cta-primary"],
        },
        padding: '8px',
        cursor: 'pointer'
    }),
    menu: (provided: any) => ({
        ...provided,
        backgroundColor: colors["ui-background-elevated-component"], // Explicitly set to white in light mode
        boxShadow: `0px 4px 12px ${colors["unlisted-scrim-15"]}`
    }),
    input: (provided: any) => ({
        ...provided,
        color: colors["unlisted-dropdown-text"]
    }),
    indicatorSeparator: (provided: any) => ({
        ...provided,
        backgroundColor: colors["unlisted-gray-lines"]
    }),
}

export const navTimezoneStyle = {
    ...timezoneStyle,
    control: (provided: any, state: any) => ({
        ...timezoneStyle.control(provided, state),
        // Teal T10 rather than the filters' T20: at the top of every page this
        // chip is always on screen, and T20 read too strong there.
        backgroundColor: TEAL_T10,
        borderRadius: '3px',
    }),
    singleValue: (provided: any) => ({
        ...timezoneStyle.singleValue(provided),
        color: SELECTED_FILTER_CHIP.color,
        fontFamily: '"SF Pro", -apple-system, BlinkMacSystemFont, sans-serif',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 'normal',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '100%',
    }),
    input: (provided: any) => ({
        ...timezoneStyle.input(provided),
        color: SELECTED_FILTER_CHIP.color,
        fontFamily: '"SF Pro", -apple-system, BlinkMacSystemFont, sans-serif',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 'normal',
    }),
};

// The left navigation rail's timezone picker. The rail is only 110px wide and
// sits against the left edge of the screen, so the menu has to break OUT of it
// to the right (left:0 / right:auto) and carry its own width — inheriting the
// rail's width shrink-wraps the option list down to unreadable.
export const navTimezoneRailStyle = {
    ...navTimezoneStyle,
    menu: (provided: any) => ({
        ...navTimezoneStyle.menu(provided),
        left: 0,
        right: 'auto',
        width: '300px',
        maxWidth: 'calc(100vw - 120px)',
    }),
};

export const navTimezoneMobileStyle = {
    ...navTimezoneStyle,
    menu: (provided: any) => ({
        ...navTimezoneStyle.menu(provided),
        right: 'auto',
        left: 0,
    }),
    menuPortal: (provided: any) => ({
        ...provided,
        zIndex: 9999,
    }),
};

export const navLanguageMobileStyle = {
    ...languageSelectStyle,
    menu: (provided: any) => ({
        ...languageSelectStyle.menu(provided),
        right: 'auto',
        left: 0,
    }),
    menuPortal: (provided: any) => ({
        ...provided,
        zIndex: 9999,
    }),
};

export const routeFilterStyle = {
    option: (provided: any, state: any) => ({
        ...provided,
        backgroundColor: state.isSelected ? OPTION_SELECTED : state.isFocused ? OPTION_HOVER : colors["ui-background-elevated-component"],
        color: state.isSelected ? colors["unlisted-dropdown-selected-text"] : colors["unlisted-dropdown-text"],
        cursor: "pointer",
        ":hover": {
            backgroundColor: state.isSelected ? OPTION_SELECTED : OPTION_HOVER,
        }
    }),
    control: (provided: any, state: any) => ({
        ...provided,
        minHeight: 38,
        height: 38,
        backgroundColor: colors["ui-background-elevated-component"],
        borderColor: filterBorder(state.hasValue),
        borderRadius: "3px",
        boxShadow: state.isFocused ? `0 0 0 0.25px ${filterBorder(state.hasValue)}` : "none",
        "&:hover": {
            borderColor: filterBorder(state.hasValue),
        },
        transition: "border-color 0.2s ease, box-shadow 0.2s ease"
    }),
    valueContainer: (provided: any, state: any) => ({
        ...provided,
        padding: "0px 8px",
        height: 30,
        alignItems: 'center',
        // The left half of the chip; the clear "x" below is the right half.
        ...(state.hasValue ? SELECTED_FILTER_CHIP : {}),
        backgroundColor: state.hasValue ? SELECTED_FILTER_CHIP.backgroundColor : 'transparent',
        borderRadius: state.hasValue ? '3px 0 0 3px' : '0',
        margin: state.hasValue ? '3px 0 3px 3px' : '0',
    }),
    menu: (provided: any) => ({
        ...provided,
        minWidth: 300,
        backgroundColor: colors["ui-background-elevated-component"],
        boxShadow: `0px 4px 12px ${colors["unlisted-scrim-15"]}`
    }),
    // This variant portals its menu to document.body, where react-select's default
    // z-index is 1 — behind every fixed panel in the app (the add-destination
    // drawer is z-60), which made the dropdown look like it never opened.
    menuPortal: (provided: any) => ({
        ...provided,
        zIndex: 9999,
    }),
    input: (provided: any) => ({
        ...provided,
        color: colors["unlisted-dropdown-text"]
    }),
    indicatorSeparator: (provided: any) => ({
        ...provided,
        backgroundColor: colors["unlisted-gray-lines"]
    }),
    singleValue: (provided: any) => ({
        ...provided,
        ...SELECTED_FILTER_CHIP,
        backgroundColor: 'transparent',
    }),
    clearIndicator: (provided: any) => ({
        ...provided,
        ...SELECTED_FILTER_CHIP,
        '&:hover': { color: SELECTED_FILTER_CHIP.color },
        padding: '0 8px',
        display: 'flex',
        alignItems: 'center',
        height: 30,
        margin: '3px 3px 3px 0',
        borderRadius: '0 3px 3px 0',
    }),
    dropdownIndicator: (provided: any) => ({
        ...provided,
        color: colors["unlisted-secondary-gray"],
        '&:hover': {
            color: colors["ui-cta-primary"],
        },
        padding: '8px',
        cursor: 'pointer'
    }),
}